import { Navigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useEffect, useState } from 'react';
import { supabase } from '../lib/supabase';
import toast from 'react-hot-toast';

/**
 * Protected Route Component for Professional Clients
 * Redirects to login if not authenticated
 * Redirects to home if not a professional account
 */
const ProtectedProfessionalRoute = ({ children }) => {
  const { user, loading } = useAuth();
  const [verifying, setVerifying] = useState(true);
  const [isProfessional, setIsProfessional] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  useEffect(() => {
    const verifyProfessionalAccount = async () => {
      if (!user) {
        setVerifying(false);
        return;
      }

      try {
        // Check user_metadata for account_type
        const accountType = user?.user_metadata?.account_type;
        
        if (accountType !== 'professional') {
          setErrorMessage('Ce compte n\'est pas un compte professionnel. Veuillez vous inscrire en tant que client professionnel.');
          setIsProfessional(false);
          setVerifying(false);
          return;
        }

        // Verify professional_clients record exists
        const { data: professionalClient, error } = await supabase
          ?.from('professional_clients')
          ?.select('id, is_active')
          ?.eq('auth_user_id', user?.id)
          ?.maybeSingle();

        if (error) {
          console.error('Error verifying professional account:', error);
          setErrorMessage('Erreur lors de la vérification du compte professionnel');
          setIsProfessional(false);
          setVerifying(false);
          return;
        }

        if (!professionalClient) {
          setErrorMessage('Profil professionnel introuvable. Votre compte est en cours de création. Veuillez réessayer dans quelques instants.');
          setIsProfessional(false);
          setVerifying(false);
          return;
        }

        // Check if account is active
        if (professionalClient?.is_active === false) {
          setErrorMessage('Votre compte professionnel est inactif. Veuillez contacter le support.');
          setIsProfessional(false);
          setVerifying(false);
          return;
        }

        // All checks passed
        setIsProfessional(true);
        setVerifying(false);
      } catch (error) {
        console.error('Error verifying professional account:', error);
        setErrorMessage('Erreur lors de la vérification du compte');
        setIsProfessional(false);
        setVerifying(false);
      }
    };

    verifyProfessionalAccount();
  }, [user]);

  // Show loading state while checking authentication
  if (loading || verifying) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Vérification de l'authentification...</p>
        </div>
      </div>
    );
  }

  // Redirect to login if not authenticated
  if (!user) {
    return <Navigate to="/professional-client-login" replace />;
  }

  // Check if user is professional
  if (!isProfessional) {
    // Show error toast and redirect
    if (errorMessage) {
      toast?.error(errorMessage, { duration: 5000 });
    }
    
    // Sign out the user since they can't access this area
    supabase?.auth?.signOut();
    
    return <Navigate to="/professional-client-login" replace />;
  }

  // User is authenticated and has professional account type
  return children;
};

export default ProtectedProfessionalRoute;